import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { useTranslation } from 'react-i18next';

import FeedbackAction from '~/components/Actions/FeedbackAction';

import usePublication from '~/hooks/usePublication';

import getPublication from '~/services/api/publication/getPublication.server';

import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';

import {
  ResultsStyleBulletPoints,
  ResultsStyleDefault,
  ResultsStyleInline,
  ResultsStyleNumberedList,
  ResultsStyleUnstyled,
} from './_ResultsStyle';

import { loaderSession } from '~/session';

interface PollChoice {
  id: string;
  label: string;
}

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    overrides: {
      robots: 'noindex',
    },
  });
};

export const handle = {
  dynamicLinks: ({ data }: any) => {
    if (!data?.requestUrl) return [];
    return [{ rel: 'canonical', href: getCanonicalUrl(data.requestUrl) }];
  },
};

export const loader: LoaderFunction = async ({ request, params }) => {
  const { api, setHeaders, session } = await loaderSession(request);

  // A user should not be able to post a response if publication does not have polls enabled
  const publication = await getPublication(request, session);
  if (!publication.has_polls) {
    return redirect('/');
  }

  const pollId = params.pollId;
  const url = new URL(request.url);
  const searchParams = url.searchParams;
  const pcid = searchParams.get('pcid');
  const ppid = searchParams.get('ppid');
  const sid = searchParams.get('sid');

  try {
    const res = await api.get('/polls/results', {
      params: {
        poll_id: pollId,
        pcid,
        ppid,
        sid,
      },
      headers: setHeaders(),
    });

    return json({
      publication,
      requestUrl: url.href,
      poll: res.data.poll,
      fallbackRedirectUrl: res.data.fallback_redirect_url,
      pollChoices: res.data.poll_choices,
      metrics: res.data.metrics,
      pollResponse: {
        pcid,
        ppid,
        sid,
      },
    });
  } catch {
    throw new Response('Not Found', {
      status: 404,
    });
  }
};

const Results = () => {
  const { poll, fallbackRedirectUrl, pollChoices, pollResponse, metrics } = useLoaderData<typeof loader>();
  const publication = usePublication();

  const isTrivia = poll.poll_type === 'trivia';
  const triviaAnswerId =
    isTrivia && poll.trivia_answer
      ? pollChoices.find((choice: PollChoice) => choice.label === poll.trivia_answer)?.id
      : '';
  const isCorrect = triviaAnswerId === pollResponse.pcid;

  const hasLiveMetrics = metrics.length > 0;
  const { t } = useTranslation();

  return (
    <div className="flex-auto py-0 sm:py-12">
      <div className="mx-auto max-w-2xl px-0 sm:px-8">
        <div className="w-full text-gray-900">
          <div className="h-full overflow-x-hidden overflow-y-scroll bg-white sm:h-auto sm:divide-y sm:divide-gray-200 sm:rounded-lg sm:border">
            <div className="space-y-6 p-4 sm:p-8">
              {/* HEADER */}
              <div className="flex flex-col items-center justify-between space-y-2">
                <div>
                  {(isTrivia && isCorrect) || !isTrivia ? (
                    <CheckCircleIcon className="h-10 w-10 text-green-500 sm:h-6 sm:w-6" />
                  ) : (
                    <XCircleIcon className="h-10 w-10 text-red-500 sm:h-6 sm:w-6" />
                  )}
                </div>
                <h1 className="text-xl">
                  {isTrivia
                    ? isCorrect
                      ? t('polls.response.trivia.correct')
                      : t('polls.response.trivia.incorrect')
                    : t('polls.response.success')}
                </h1>
              </div>

              {/* RESULTS */}
              <div className="mb-8 rounded border p-4">
                <h2 className="mx-auto mb-2 font-extrabold">{poll.question}</h2>
                {poll.poll_style === 'default' && (
                  <ResultsStyleDefault
                    pollChoices={pollChoices}
                    pollResponse={pollResponse}
                    triviaAnswerId={triviaAnswerId}
                    metrics={metrics}
                    isTrivia={isTrivia}
                    hasLiveMetrics={hasLiveMetrics}
                  />
                )}
                {poll.poll_style === 'bullet_points' && (
                  <ResultsStyleBulletPoints
                    pollChoices={pollChoices}
                    pollResponse={pollResponse}
                    triviaAnswerId={triviaAnswerId}
                    metrics={metrics}
                    isTrivia={isTrivia}
                    hasLiveMetrics={hasLiveMetrics}
                  />
                )}
                {poll.poll_style === 'numbered_list' && (
                  <ResultsStyleNumberedList
                    pollChoices={pollChoices}
                    pollResponse={pollResponse}
                    triviaAnswerId={triviaAnswerId}
                    metrics={metrics}
                    isTrivia={isTrivia}
                    hasLiveMetrics={hasLiveMetrics}
                  />
                )}
                {poll.poll_style === 'unstyled' && (
                  <ResultsStyleUnstyled
                    pollChoices={pollChoices}
                    pollResponse={pollResponse}
                    triviaAnswerId={triviaAnswerId}
                    metrics={metrics}
                    isTrivia={isTrivia}
                    hasLiveMetrics={hasLiveMetrics}
                  />
                )}
                {poll.poll_style === 'inline' && (
                  <ResultsStyleInline
                    pollChoices={pollChoices}
                    pollResponse={pollResponse}
                    triviaAnswerId={triviaAnswerId}
                    metrics={metrics}
                    isTrivia={isTrivia}
                    hasLiveMetrics={hasLiveMetrics}
                  />
                )}
              </div>

              {/* FORM */}
              <FeedbackAction
                ppid={pollResponse.ppid}
                pcid={pollResponse.pcid}
                sid={pollResponse.sid}
                redirectLink={poll.redirect_url ? poll.redirect_url : fallbackRedirectUrl}
              />

              {/* BEEHIIV CALLOUT */}
              {publication.beehiiv_branding && <PoweredBy utmSource={publication.name} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface PoweredByProps {
  utmSource: string;
}

const PoweredBy = ({ utmSource }: PoweredByProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex justify-center">
      <a
        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-6 py-3 text-xs font-medium text-gray-700 shadow-sm duration-200 hover:bg-gray-50 focus:outline-none"
        data-skip-tracking="true"
        href={`https://www.beehiiv.com?utm_medium=poll_response&utm_source=${utmSource}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <svg
          className="mr-2"
          id="a"
          style={{ height: 20 }}
          version="1.1"
          viewBox="0 0 132.2 110.2"
          x="0px"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          xmlns="http://www.w3.org/2000/svg"
          y="0px"
        >
          <g id="b">
            <g id="c">
              <g>
                <g id="d">
                  <g>
                    <path
                      className="st0"
                      d="M101.7,51.8H15.3c-2.2,0-4-1.8-4-4v-6.1c0-6.9,5.6-12.5,12.5-12.5h69.3c6.9,0,12.5,5.6,12.5,12.5v6.1 C105.7,50,103.9,51.8,101.7,51.8z"
                      fill="currentColor"
                    />
                    <path
                      className="st0"
                      d="M83.5,22.6h-50c-2.2,0-4.1-1.8-4.1-4.1l0,0C29.4,8.3,37.7,0,47.9,0h21.2c10.2,0,18.5,8.3,18.5,18.5l0,0 C87.6,20.8,85.8,22.6,83.5,22.6C83.5,22.6,83.5,22.6,83.5,22.6z"
                      fill="currentColor"
                    />
                    <path
                      className="st0"
                      d="M105.7,58.4H11.3C5.1,58.4,0,63.5,0,69.7S5.1,81,11.3,81h33.5c1.2-6.4,6.8-11.3,13.6-11.3 S70.9,74.6,72.1,81h33.5c6.2,0,11.3-5.1,11.3-11.3S111.9,58.4,105.7,58.4C105.7,58.4,105.7,58.4,105.7,58.4z"
                      fill="currentColor"
                    />
                    <g>
                      <path
                        className="st0"
                        d="M101.6,87.6H72.3v22.6h22c6.2,0,11.3-5.1,11.3-11.3v-7.2C105.7,89.5,103.9,87.6,101.6,87.6 C101.6,87.6,101.6,87.6,101.6,87.6z"
                        fill="currentColor"
                      />
                      <path
                        className="st0"
                        d="M15.3,87.6c-2.2,0-4.1,1.8-4.1,4.1v7.2c0,6.2,5.1,11.3,11.3,11.3h22V87.6C44.6,87.6,15.3,87.6,15.3,87.6z "
                        fill="currentColor"
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </svg>
        {t('shared.cta.powered_by_beehiiv')}
      </a>
    </div>
  );
};

export default Results;
